import {CarmenGeojsonFeature, MaplibreGeocoderFeatureResults} from '@maplibre/maplibre-gl-geocoder';
import {BBox, Feature, Polygon} from 'geojson';

type SearchConfig = {
    query?: string | number[];
    countries?: string;
};

export async function findPlaces(searchConfig: SearchConfig) {
    const features: CarmenGeojsonFeature[] = [];
    try {
        if (!searchConfig.query) {
            return featuresToMapLibreGeocoderFeatureResults(features);
        }

        const params = new URLSearchParams({
            q: String(searchConfig.query),
            format: 'geojson',
            polygon_geojson: '1'
        });

        if (searchConfig.countries) {
            params.append('countrycodes', searchConfig.countries);
        }

        const request = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
        const response = await fetch(request);
        const geoJson = await response.json();

        if (!isValidPlaceSearchResponse(geoJson)) {
            return featuresToMapLibreGeocoderFeatureResults(features);
        }

        for (const feature of geoJson.features) {
            const center = [
                feature.bbox[0] +
                (feature.bbox[2] - feature.bbox[0]) / 2,
                feature.bbox[1] +
                (feature.bbox[3] - feature.bbox[1]) / 2
            ];
            const point: CarmenGeojsonFeature = {
                type: 'Feature',
                id: String(feature.properties.osm_id),
                geometry: {
                    type: 'Point',
                    coordinates: center
                },
                place_name: feature.properties.display_name,
                properties: feature.properties,
                text: feature.properties.display_name,
                place_type: ['place'],
                bbox: [feature.bbox[0], feature.bbox[1], feature.bbox[2], feature.bbox[3]]
            };
            features.push(point);
        }
    } catch (e) {
        // eslint-disable-next-line
        console.error(`Failed to findPlaces with error: ${e}`);
    }

    return featuresToMapLibreGeocoderFeatureResults(features);
}

export async function findPlacesByGeocode() {
    // NOOP
    return featuresToMapLibreGeocoderFeatureResults([]);
}

function isValidPlaceSearchResponse(resp: unknown): resp is PlaceSearchFeatureCollection {
    return (
        typeof resp === 'object' &&
        resp !== null &&
        'type' in resp &&
        resp.type === 'FeatureCollection' &&
        'features' in resp &&
        Array.isArray(resp.features) &&
        resp.features.every(isValidPlaceSearchFeature)
    );
}

function isValidPlaceSearchFeature(feature: unknown): feature is Feature<Polygon, PlaceSearchProperties> {
    return feature !== null && feature !== undefined && typeof feature === 'object' && 'geometry' in feature && 'properties' in feature;
}

function featuresToMapLibreGeocoderFeatureResults(features: CarmenGeojsonFeature[]): MaplibreGeocoderFeatureResults {
    return {
        type: 'FeatureCollection',
        features: features
    };
}

interface PlaceSearchFeatureCollection {
    type: string;
    licence: string;
    features: PlaceSearchFeature[];
}

interface PlaceSearchFeature extends Feature<Polygon, PlaceSearchProperties> {
    bbox: BBox;
}

interface PlaceSearchProperties {
    place_id: number;
    osm_type: string;
    osm_id: number;
    place_rank: number;
    category: string;
    type: string;
    importance: number;
    addresstype: string;
    name: string;
    display_name: string;
}
